import React, { useCallback, useState } from "react";
import Grid from "@material-ui/core/Grid";
import { IconButton } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { withNamespace } from "components/Form/FormikControls";
import { useFieldState, useFieldValue } from "hooks";
import { CheckComparator } from "containers/Settings/RuleBooks/constants";
import {
  getDataTypeByPath,
  normalisePathParts,
} from "containers/Settings/RuleBooks/Schema/schema";
import Button from "@material-ui/core/Button";
import { useRuleFieldSchema } from "containers/Settings/RuleBooks/Schema/schemaContext";
import {
  SchemaFieldDataSource,
  SchemaFieldDataSourceField,
} from "containers/Settings/RuleBooks/DataSource/SchemaFieldDataSource";
import { Criteria } from "./WhenClause";
import { AddCriterionModal } from "./AddCriterionModal";

interface Check {
  comparator: ValueInObj<typeof CheckComparator>;
  field: string;
  values: Array<number | string>;
}

interface Criterion {
  checkGroups: Array<Array<Check>>;
  query: SchemaFieldDataSourceField;

  // Form Field used to show and clear the "Using Related Data" section
  usingQueryData?: true;
}
export interface CriteriaFormValues {
  criteria: Array<Criterion>;
  input_field: string | null;
}

interface CriteriaFormProps {
  // The datatype the RuleSet's the input_field (if set) refers to
  // the field type must be present in the LivestockSale Export Schema
  parentInputPath: string | null;

  hasPrerequisiteRule: boolean;

  namespace?: string;

  readOnly?: boolean;
}

export function CriteriaForm(props: CriteriaFormProps) {
  const {
    namespace: ns = "",
    parentInputPath,
    hasPrerequisiteRule,
    readOnly,
  } = props;

  const [isAddFilterModalOpen, setIsAddFilterModalOpen] = useState(false);

  const openAddFilterModal = useCallback(
    () => setIsAddFilterModalOpen(true),
    [setIsAddFilterModalOpen],
  );

  const closeAddFilterModal = useCallback(
    () => setIsAddFilterModalOpen(false),
    [setIsAddFilterModalOpen],
  );

  const [criteria, setCriteria] = useFieldState(withNamespace(ns, "criteria"));
  const ruleInputPath = useFieldValue(withNamespace(ns, "input_field.value"));

  const isUsingParent = Boolean(!ruleInputPath && parentInputPath);

  const [showNestedDataPicker, setShowNestedDataPicker] =
    useState(!isUsingParent);

  const schema = useRuleFieldSchema();

  const parentDataType = getDataTypeByPath(schema, parentInputPath);

  const fullPath = normalisePathParts([parentInputPath, ruleInputPath]);

  function addCriterion(criterion: Criterion) {
    setCriteria([...criteria, criterion]);
  }

  function useNestedData() {
    setShowNestedDataPicker(true);
  }

  let prefix = fullPath;
  if (parentInputPath && !hasPrerequisiteRule) {
    prefix = "the Rule Set's";
  } else if (parentInputPath) {
    prefix = "the Prerequisite Rule's";
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {!readOnly && "Create "}Ledger Entries based on
          {parentInputPath && (
            <>
              {" "}
              {prefix}{" "}
              <Button disabled>
                {ruleInputPath
                  ? parentDataType.possessive
                  : parentDataType.plural}
              </Button>
            </>
          )}
          {showNestedDataPicker ? (
            <SchemaFieldDataSource
              fieldType={null}
              namespace={withNamespace(ns, "input_field")}
              fieldPath={parentInputPath}
              allowSelectManyRelations
              schema={schema}
              showManyRelations
              showRelations
              dataTour="rule-input-field"
              readOnly={readOnly}
            />
          ) : (
            <Button
              onClick={useNestedData}
              endIcon={<FontAwesomeIcon icon={faChevronRight} />}
              data-tour="use-nested-data"
              disabled={readOnly}
            />
          )}
          {!readOnly && (
            <IconButton
              aria-label="Add Criteria"
              onClick={openAddFilterModal}
              disabled={!fullPath}
              title={
                fullPath
                  ? "Apply a filter to the data"
                  : "You must select a dataset before you can add a filter"
              }
            >
              <FontAwesomeIcon icon={faPlus} size="xs" />
              &nbsp;Criteria
            </IconButton>
          )}
        </Grid>

        <Grid item xs={12}>
          <Criteria
            namespace={withNamespace(ns, "criteria")}
            ruleInputPath={ruleInputPath}
            parentInputPath={parentInputPath}
            showPrerequisiteRuleSource={hasPrerequisiteRule}
            readOnly={readOnly}
          />
        </Grid>
      </Grid>

      <AddCriterionModal
        addCriterion={addCriterion}
        isOpen={isAddFilterModalOpen}
        closeModal={closeAddFilterModal}
        fullPath={fullPath}
      />
    </>
  );
}
